/**
 * @class
 * @function ProductCompareTable
 */
if (!customElements.get('product-compare-table')) {
    class ProductCompareTable extends HTMLElement {
      constructor() {
        super();
      }
  
      connectedCallback() {
        this.container = this.querySelector('.compare-lightbox--data');
        this.addEventListeners();
      }

      disconnectedCallback() {
        if (this.scrollContainerEl && this.scrollHandler) {
          this.scrollContainerEl.removeEventListener('scroll', this.scrollHandler);
        }
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }
      }
  
      addEventListeners() {
        this.addEventListener('render-product-compare-modal', this.renderTable.bind(this));
      }
  
      renderTable() {
        this.classList.add('loading');
        this.handles = this.getCompareHandles();
        this.fetchProducts();
      }

      getSelectedProducts() {
          const panel = document.querySelector('product-compare-panel');
          return panel ? panel.getSelectedProducts() : [];
      }      
      getCompareHandles() {
        const { searchParams } = new URL(window.location);
        const handles = searchParams.get('compare');
        if (typeof handles === 'string' && handles !== '') {
          return handles.split(',');
        }
        return [];
      }

      async fetchProducts() {
        let compareProducts = this.getSelectedProducts(),
          returnHtml = '',
          returnArray = [];
  
        await Promise.all(compareProducts.map(async (product) => {
          await fetch(`${product.url}?view=compare`, {
            method: 'GET'
          }).then((response) => {
            return response.text();
          }).then((response) => {
            returnArray[product.id] = this.getSectionInnerHTML(response, '.compare-lightbox--data');
          });
        })).finally(() => {
          this.classList.remove('loading');
        });
        compareProducts.forEach((product, i) => {
          returnHtml += returnArray[product.id];
        });
        this.container.style.setProperty('--columns', compareProducts.length);
        this.container.innerHTML = returnHtml;
  
        // ----------------------------------------------------
        // TRIGGER STICKY BAR AFTER DOM RENDERING IS DONE
        // ----------------------------------------------------
        this.buildStickyBar();

        this.equalHeights();
      }

      getSectionInnerHTML(html, selector) {
        return new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(selector).innerHTML;
      }

      buildStickyBar() {
        if (this.scrollContainerEl && this.scrollHandler) {
          this.scrollContainerEl.removeEventListener('scroll', this.scrollHandler);
        }
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }

        const oldBar = this.querySelector('.compare-sticky-bar');
        if (oldBar) oldBar.remove();

        const tables = this.querySelectorAll('.compare-lightbox--table');
        if (!tables.length) return;

        const bar = document.createElement('div');
        bar.className = 'compare-sticky-bar';
        
        bar.style.cssText = `
          position: sticky;
          top: -2px;
          left: 0;
          z-index: 1010;
          width: 100%;
          box-sizing: border-box;
          opacity: 0;
          pointer-events: none;
          visibility: hidden;
          transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
        `;

        const inner = document.createElement('div');
        inner.className = 'compare-sticky-bar-inner';
        
        inner.style.cssText = `
          display: grid;
          grid-template-columns: repeat(${tables.length}, minmax(0, 1fr));
          width: 100%;
          min-width: 100%;
          padding: 10px 0;
          gap: 0;
          box-sizing: border-box;
        `;

        tables.forEach(table => {
          const col = document.createElement('div');
          
          col.style.cssText = `
            min-width: 0;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 0;
          `;

          const title = table.querySelector('.product-card-title') || 
                        table.querySelector('.product-title') || 
                        table.querySelector('a[href*="/products/"]');
          
          const swatches = table.querySelector('.product-card-swatches--container') || 
                           table.querySelector('.swatches') || 
                           table.querySelector('.color-swatches');

          if (title) {
            const t = document.createElement('a');
            t.href = title.getAttribute('href') || '#';
            t.textContent = title.textContent.trim();
            t.style.cssText = 'display: block; width: 100%; text-align: center; font-weight: 600; font-size: 12px; margin-bottom: 2px; color: #2c2d2e; text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
            col.appendChild(t);
          }

          if (swatches) {
            col.appendChild(swatches.cloneNode(true));
          }
          inner.appendChild(col);
        });

        bar.appendChild(inner);
        this.insertBefore(bar, this.firstChild);

        this.alignStickyBarToTable(bar, inner, tables);

        this.resizeHandler = () => {
          this.alignStickyBarToTable(bar, inner, tables);
        };
        window.addEventListener('resize', this.resizeHandler);

        const scrollContainer = this.closest('.product-compare-modal__content-info') || this.closest('.product-compare-modal__content');
        const modalContent = this.closest('.product-compare-modal__content');
        const cardHeaderRow = this.querySelector('.compare-lightbox--table .compare-lightbox--row:first-child');

        if (scrollContainer && cardHeaderRow) {
          this.scrollContainerEl = scrollContainer;
          this.scrollHandler = () => {
            const cardHeaderRect = cardHeaderRow.getBoundingClientRect();
            const scrollContainerRect = scrollContainer.getBoundingClientRect();
            
            const cardHeight = cardHeaderRect.height;
            const triggerPoint = cardHeaderRect.top + (cardHeight * 0.70);

            const stickyVisible = triggerPoint <= scrollContainerRect.top;

            if (stickyVisible) {
              bar.style.opacity = '1';
              bar.style.pointerEvents = 'auto';
              bar.style.visibility = 'visible';
            } else {
              bar.style.opacity = '0';
              bar.style.pointerEvents = 'none';
              bar.style.visibility = 'hidden';
            }

            if (modalContent) {
              modalContent.classList.toggle('compare-modal--compact-header', stickyVisible);
            }
          };

          scrollContainer.addEventListener('scroll', this.scrollHandler);
          this.scrollHandler();
        }
      }

      alignStickyBarToTable(bar, inner, tables) {
        if (!tables.length || !this.container) return;

        const firstTable = tables[0];
        const lastTable = tables[tables.length - 1];

        if (!firstTable || !lastTable) return;

        const hostRect = this.getBoundingClientRect();
        const firstRect = firstTable.getBoundingClientRect();
        const lastRect = lastTable.getBoundingClientRect();
        const tableRects = Array.from(tables).map((table) => table.getBoundingClientRect());
        const tableWidths = tableRects.map((rect) => rect.width);
        const columnGaps = [];

        for (let i = 0; i < tableRects.length - 1; i++) {
          const gap = tableRects[i + 1].left - tableRects[i].right;
          columnGaps.push(gap > 0 ? gap : 0);
        }

        const averageGap = columnGaps.length
          ? columnGaps.reduce((sum, gap) => sum + gap, 0) / columnGaps.length
          : 0;

        const firstLeft = firstRect.left - hostRect.left;
        const totalWidth = lastRect.right - firstRect.left;

        if (!totalWidth || tableWidths.some((width) => width <= 0)) return;

        inner.style.columnGap = `${averageGap}px`;
        inner.style.gridTemplateColumns = tableWidths.map((width) => `${width}px`).join(' ');
        inner.style.width = `${totalWidth}px`;
        inner.style.minWidth = `${totalWidth}px`;
        bar.style.left = `${firstLeft}px`;
        bar.style.width = `${totalWidth}px`;
        bar.style.minWidth = `${totalWidth}px`;
      }

      equalHeights() {
        for (let i = 0; i < 10; i++) {
          this.equalizeTableRowHeightAcrossTables(i);
        }
      }

      equalizeTableRowHeightAcrossTables(rowPosition) {
        const tables = this.container.querySelectorAll('.compare-lightbox--table');
        const heights = [];
  
        tables.forEach(table => {
          const rows = table.querySelectorAll('.compare-lightbox--row');
          if (rows.length > rowPosition) {
            const rowHeight = rows[rowPosition].clientHeight;
            if (heights.length === 0) {
              heights[rowPosition] = rowHeight;
            } else {
              if (rowHeight > heights[rowPosition]) {
                heights[rowPosition] = rowHeight;
              }
            }
          }
        });

        tables.forEach(table => {
          const rows = table.querySelectorAll('.compare-lightbox--row');
          if (rows.length > rowPosition) {
            rows[rowPosition].style.height = heights[rowPosition] + 'px';
          }
        });
      }
    }
    customElements.define('product-compare-table', ProductCompareTable);
  }